import { AxiosInstance, AxiosResponse } from 'axios'
import IDataComponent from 'src/models/IDataComponent'
import IPipeline, { PipelineStatus, PipelineType } from 'src/models/IPipeline'
import IWorkspace from 'src/models/IWorkspace'
import { componentPluginType, INGESTION_PLUGIN_TYPES } from 'src/utils/pipelineType'

/**
 * How a pipeline becomes an ingestion (MEL-702): it keeps its extractor, mappers and loader, and so
 * its ID and sync state; the other tools it runs - dbt, utilities - move to a new workflow that runs
 * after it. Used to convert a workflow, and to fix a draft ingestion its pipeline file added dbt to.
 *
 * Plan from the WHOLE pipeline (`GET pipeline`): the pipelines list has no properties, and its
 * components carry no plugin, so actions written with plugin names (`dbt-postgres:run`) would not
 * be matched to their component.
 */
export interface IIngestionSplit {
    /** stays in the pipeline, source first */
    keep: IDataComponent[]
    /** tools the pipeline runs, which move to the new workflow */
    move: IDataComponent[]
    /** tools the pipeline holds but never runs (its actions don't name them): removed, not moved */
    drop: IDataComponent[]
    /** the new workflow's label, unique in the workspace */
    workflowLabel: string
    /** pipelines that run after this one: they should run after the new workflow instead */
    downstream: IPipeline[]
    /** why the split can't be made; empty when it can */
    blockers: string[]
}

/** As the API names a pipeline from its label (`PipelineResource.normaliseName`). */
export const normalisePipelineName = (label: string): string => label.trim().replace(/[\s+]+/g, '-').toLowerCase()

const count = (components: IDataComponent[], pluginType: string) =>
    components.filter(dc => componentPluginType(dc) === pluginType).length

const uniqueLabel = (label: string, pipelines: IPipeline[]) => {
    const taken = new Set(pipelines.reduce<string[]>((names, p) => [...names, p.name, normalisePipelineName(p.label || p.name)], []))
    let candidate = label
    for (let i = 2; taken.has(normalisePipelineName(candidate)); i++)
        candidate = `${label} ${i}`
    return candidate
}

/** the component an action runs: `dbt`, `dbt:run`, `dbt-postgres:run` (plugin name) … */
const actionTarget = (action: string) => action.split(':')[0]
const isActionOf = (action: string, dc: IDataComponent) =>
    actionTarget(action) === dc.name || actionTarget(action) === dc._embedded?.dataplugin?.name

const actionsOf = (pipeline: IPipeline, components: IDataComponent[]) =>
    (pipeline.actions || []).filter(a => components.some(dc => isActionOf(a, dc)))

const propertiesOf = (pipeline: IPipeline, components: IDataComponent[]) =>
    Object.entries(pipeline.properties || {})
        .filter(([k]) => components.some(dc => k.startsWith(`${dc.name}.`)))
        .reduce((p, [k, v]) => ({ ...p, [k]: v }), {} as { [property: string]: string })

export const planIngestionSplit = (pipeline: IPipeline, pipelines: IPipeline[]): IIngestionSplit => {
    const components = pipeline._embedded?.dataComponents || []
    const isIngestionComponent = (dc: IDataComponent) => INGESTION_PLUGIN_TYPES.includes(componentPluginType(dc) || '')

    const keep = components
        .filter(isIngestionComponent)
        .sort((a, b) => INGESTION_PLUGIN_TYPES.indexOf(componentPluginType(a)!) - INGESTION_PLUGIN_TYPES.indexOf(componentPluginType(b)!))
    const others = components.filter(dc => !isIngestionComponent(dc))

    // with no actions every component runs; with actions only the ones they name do - since V4.045 most
    // pipelines hold a dbt component, and moving one that never ran would start running it
    const actions = pipeline.actions || []
    const runs = (dc: IDataComponent) => !actions.length || actions.some(a => isActionOf(a, dc))
    const move = others.filter(runs)
    const drop = others.filter(dc => !runs(dc))

    const blockers: string[] = []
    const extractors = count(components, 'EXTRACTOR')
    const loaders = count(components, 'LOADER')

    if (extractors !== 1)
        blockers.push(extractors ? `It has ${extractors} sources; an ingestion copies one.` : 'It has no source to copy.')
    if (loaders !== 1)
        blockers.push(loaders ? `It has ${loaders} data stores; an ingestion loads into one.` : 'It has no data store to load into.')
    if (pipeline.script)
        blockers.push('It runs a custom script. Replace the script with actions first.')
    if (actionsOf(pipeline, keep).some(a => a.includes(':')))
        blockers.push('It runs commands of its source or data store, which an ingestion can\'t.')

    const otherPipelines = pipelines.filter(p => p.id !== pipeline.id)
    const label = pipeline.label || pipeline.name
    const workflowLabel = uniqueLabel(`${label} ${move[0]?.name || 'workflow'}`, otherPipelines)
    const downstream = otherPipelines.filter(p => (p.triggeredBy || []).some(t => t.toLowerCase() === pipeline.name.toLowerCase()))

    return { keep, move, drop, workflowLabel, downstream, blockers }
}

export interface IIngestionSplitResult {
    ingestion: IPipeline
    /** the new workflow, unless there was nothing to move */
    workflow?: IPipeline
    /** set when the workflow could not be published, and was left a draft */
    workflowError?: string
    /** downstream pipelines that could not be moved to run after the workflow, with why */
    downstreamErrors: string[]
}

export const errorMessage = (e: any): string => {
    const data = e?.response?.data
    return (Array.isArray(data) ? data[0]?.defaultMessage : data?.message || data?.detail) || e?.message || 'Unknown error'
}

const put = (api: AxiosInstance, href: string, data: Partial<IPipeline>) =>
    api.put(href, data).then((r: AxiosResponse<IPipeline>) => r.data)

/** what a save sends back for a pipeline, given its new triggers */
const asSaved = (p: IPipeline, triggeredBy: string[]): Partial<IPipeline> => ({
    type: p.type,
    label: p.label,
    schedule: p.schedule,
    timeout: p.timeout,
    maxRetries: p.maxRetries,
    script: p.script,
    actions: p.actions,
    dataComponents: p.dataComponents,
    properties: p.properties,
    triggeredBy,
})

/**
 * Makes the split planned from `pipeline`, the whole pipeline. The workflow is created as a draft
 * first, so nothing runs twice and nothing is lost if the pipeline can't be converted: that draft
 * is then deleted. A pipeline that was a draft stays one, and so does its new workflow; otherwise
 * the workflow is published, and if that fails it stays a draft for the user to finish.
 * With `moveDownstream`, the pipelines that ran after this one run after the workflow instead, so
 * they still see what dbt made.
 */
export const splitIntoIngestion = async (
    api: AxiosInstance, workspace: IWorkspace, pipeline: IPipeline, split: IIngestionSplit,
    options?: Partial<{ moveDownstream: boolean }>,
): Promise<IIngestionSplitResult> => {
    const isDraft = pipeline.status === PipelineStatus.Draft
    const saveLink = (p: IPipeline) => (p.status === PipelineStatus.Draft ? p._links['draft pipeline'] : p._links['update pipeline']).href

    const convert = () => put(api, saveLink(pipeline), {
        type: PipelineType.Ingestion,
        label: pipeline.label,
        schedule: pipeline.schedule,
        timeout: pipeline.timeout,
        maxRetries: pipeline.maxRetries,
        triggeredBy: pipeline.triggeredBy,
        dataComponents: split.keep.map(dc => dc.name),
        properties: propertiesOf(pipeline, split.keep),
        // the same extract and load as before, so the state ID - and with it the sync state - stays
        actions: actionsOf(pipeline, split.keep),
    })

    if (!split.move.length)
        return { ingestion: await convert().catch(e => { throw new Error(errorMessage(e)) }), downstreamErrors: [] }

    const workflowData: Partial<IPipeline> = {
        type: PipelineType.Workflow,
        label: split.workflowLabel,
        dataComponents: split.move.map(dc => dc.name),
        actions: actionsOf(pipeline, split.move),
        properties: propertiesOf(pipeline, split.move),
        triggeredBy: [pipeline.name],
        timeout: pipeline.timeout,
        maxRetries: pipeline.maxRetries,
    }

    const template: IPipeline = await api.post(workspace._links['new pipeline'].href).then((r: AxiosResponse<IPipeline>) => r.data)
    const draft = await put(api, template._links['draft pipeline'].href, workflowData)

    let ingestion: IPipeline
    try {
        ingestion = await convert()
    } catch (e) {
        await api.delete(draft._links['delete pipeline'].href).catch(() => undefined)
        throw new Error(errorMessage(e))
    }

    let workflow = draft
    let workflowError: string | undefined
    if (!isDraft) {
        await put(api, draft._links['update pipeline'].href, workflowData)
            .then(published => { workflow = published })
            .catch(e => { workflowError = errorMessage(e) })
    }

    const downstreamErrors: string[] = []
    if (options?.moveDownstream) {
        for (const listed of split.downstream) {
            await api.get(listed._links.self.href)
                .then((r: AxiosResponse<IPipeline>) => r.data)
                .then(q => put(api, saveLink(q), asSaved(q, (q.triggeredBy || [])
                    .map(t => t.toLowerCase() === pipeline.name.toLowerCase() ? workflow.name : t))))
                .catch(e => { downstreamErrors.push(`${listed.label || listed.name}: ${errorMessage(e)}`) })
        }
    }

    return { ingestion, workflow, workflowError, downstreamErrors }
}
